import clone from 'clone';
import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { buildTranslator, CustomForm, CustomFormReportData, isInputField } from '../../../../amorg-common';
import { deleteCustomFormReport, getCustomForm, getCustomFormReport } from '../../../../api/customFormClientApi';
import { ToolBarButton, ToolBarContainer } from '../../../../designSystem/compositeComponents/ButtonToolBar';
import { SingleColumnLayout } from '../../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../../designSystem/PageHeader';
import { P } from '../../../../designSystem/typography/Body';
import { H2 } from '../../../../designSystem/typography/Heading';
import { clientAccessHelper } from '../../../../utils/clientAccessHelper';
import { CustomFormFilter, customFormHelper } from '../../../../utils/customFormHelper';
import { ProtectedPageContext } from '../../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../../_pageWrappers/MemberPageWrapper';
import { customFormFieldFactory } from '../../_components/customFormFieldFactory';
import { CustomFormTabHeader } from '../_components/CustomFormTabHeader';
import { CustomFormFiltersPanel } from './_components/CustomFormFiltersPanel';
import { PrintCustomFormReportPage } from './PrintCustomFormReportPage';



export function CustomFormReportPage():JSX.Element
{
    const {formId} = useParams<{formId:string}>();
    const [selectedMenuItemKey, setSelectedMenuItemKey] = React.useState('');
    const [formCrumb, setFormCrumb] = React.useState<{name:string, activityId:string} | null>(null);

    React.useEffect(() =>
    {
        const load = async () =>
        {
            if (formId)
            {
                const form = await getCustomForm(formId);
                setSelectedMenuItemKey(form?.category || '');
            }
        };
        load();
    }, [formId]);

    return (
        <MemberPageWrapper
            selectedMenuItemKey={selectedMenuItemKey}
            breadCrumbs={[
                {text:'Ergonomiske undersøgelser', url:'/ergonomics'},
                ...(formCrumb ? [{text:formCrumb.name, url:`/customforms/${formCrumb.activityId}`}] : []),
                {text:'Overblik', url:''}
            ]}>
            <CustomFormReportPageContent onFormChanged={setFormCrumb} />
        </MemberPageWrapper>
    );
}

type CustomFormReportPageContentProps = {
    onFormChanged:(form:{name:string, activityId:string} | null) => void,
}

function CustomFormReportPageContent(props:CustomFormReportPageContentProps):JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewCustomFormAnswerList']);

    const [redirect, setRedirect] = React.useState(false);

    const {formId} = useParams<{formId:string}>();

    const [state, setState] = React.useState<{
        isLoading:boolean, 
        form:CustomForm | null, 
        report:CustomFormReportData | null, 
        filters:CustomFormFilter[] | null
    }>({ isLoading:true, form:null, report:null, filters:null });

    React.useEffect(()=>{
        const getData = async () => {
            if(formId)
            {
                const report = await getCustomFormReport(formId);
                if(!report){
                    throw new Error('Unable to load report');
                }
        
                const form = await getCustomForm(formId);
                if(!form){
                    throw new Error('Unable to load form');
                }
                setState({ 
                    isLoading:false, 
                    form, 
                    report, 
                    filters:customFormHelper.buildFilters(form, report) });
            }
        }
        getData();
    }, []);

    function handleFilterChange(fieldId:string, value:string)
    {
        if(!state.filters)
            return;

        const newFilters = clone(state.filters);
        const filter = newFilters.find(f => f.fieldId === fieldId);

        if (filter) {
            filter.selectedValue = value;
        }

        setState({ ...state, filters:newFilters });
    }

    const filteredData: CustomFormReportData = {
        formId: state.report?.formId ?? '',
        segmentReports: []
    };

    if (state.report) {
        filteredData.segmentReports = customFormHelper.applyReportFilters(
            state.report.segmentReports,
            state.filters ?? []
        );
    }

    const translator = buildTranslator('da');
    const formName = state.form?.name ? translator(state.form?.name) : '';

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: formName,
    
    });

    React.useEffect(() =>
    {
        props.onFormChanged(state.form ? { name: formName, activityId: state.form.activityId } : null);
    }, [state.form]);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return (
        
        <>
            <PageHeader
                headerText={'Overblik'}
                headerStyle="secondary-gray"
                narrow
                buttons={
                    <ToolBarContainer>
                        <ToolBarButton
                            icon='interface_actions_synchronize'
                            tooltip= 'Gendan rapport'
                            variant= 'text'
                            onClick={async() => {
                                if(!state.form || !formId)
                                    return;

                                setState({ isLoading:true, form:state.form, report:null, filters:null });
                                await deleteCustomFormReport(formId);
                                const report = await getCustomFormReport(formId);
                                if(!report){
                                    throw new Error('Unable to load report');
                                }
                                setState({ 
                                    isLoading:false, 
                                    form:state.form, 
                                    report, 
                                    filters:customFormHelper.buildFilters(state.form, report) 
                                });
                            }}
                        />
                        <ToolBarButton
                            icon='interface_actions_print'
                            tooltip='Print'
                            variant='text'
                            onClick={printFn}
                            />
                    </ToolBarContainer>
                } />
            <SingleColumnLayout narrow>
                <>
                    {redirect && state && state.form &&
                        <Navigate to={`/customForms/${state.form.activityId}`} />
                    }

                    <LoadingPageOverlay loading={state.isLoading} />

                    <CustomFormTabHeader selectedTab='overview'
                        onClick={(tabName) => {
                            if(tabName === 'answers')
                                setRedirect(true);
                        }}
                    />
                    {state.form && state.report && state.filters &&
                        <PrintCustomFormReportPage
                            form={state.form}
                            report={state.report}
                            filters={state.filters}
                            innerRef={printRef} />        
                    }
                    
                    <H2>{formName}</H2>
                    {state.form && state.report && 
                        <>
                        <CustomFormFiltersPanel
                            form={state.form}
                            report={state.report}
                            filter={state.filters ?? []}
                            onFilterChange={handleFilterChange}
                        />
                                                       
                        
                        {state.form.fields.filter(f => isInputField(f)).map((field) => 
                            customFormFieldFactory.getReportFieldValueComponent(field, filteredData.segmentReports)
                        )}
                        </>
                    }
                </>
            </SingleColumnLayout>
            
        </>
    );
}




